import { Box, Button, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import NotificationsList from '@/cam-admin/notifications/NotificationsList';
import {
  CAM_NOTIFICATIONS,
  type CamNotification,
} from '@/cam-admin/notifications/Notifications.fixtures';

// The CAM-admin Notifications page: a global feed of recent customer activity
// across every care circle, grouped by recency. Reached from the bell in the
// control-panel app bar. Each row opens that customer's control panel on the
// tab the activity belongs to.
//
// State (items, read) lives in the parent so the bell badge reflects the same
// unread count. The envelope toggles read/unread, matching the member screen;
// read state is per-CAM in the spec, here it's local prototype state.
// Deep-links land on the relevant tab (item-level precision is an open
// question in the spec).
const NotificationsPage = ({
  items = CAM_NOTIFICATIONS,
  onChange,
  onOpen,
}: {
  items?: CamNotification[];
  onChange?: (items: CamNotification[]) => void;
  onOpen?: (n: CamNotification) => void;
}) => {
  const hasUnread = items.some((n) => !n.read);

  const markRead = (n: CamNotification) =>
    onChange?.(items.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
  const markAllRead = () =>
    onChange?.(items.map((x) => ({ ...x, read: true })));
  const toggleRead = (n: CamNotification) =>
    onChange?.(items.map((x) => (x.id === n.id ? { ...x, read: !x.read } : x)));
  // Opening a row marks it read, then follows its deep-link.
  const open = (n: CamNotification) => {
    markRead(n);
    onOpen?.(n);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
        direction='row'
      >
        <Typography variant='h3' color='primary.main'>
          Notifications
        </Typography>
        <Button
          variant='text'
          color='primary'
          disabled={!hasUnread}
          onClick={markAllRead}
        >
          Mark all as read
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Box sx={{ mb: 2, color: 'text.disabled' }}>
            <Icon icon={'bell' as never} fontSize={32} />
          </Box>
          <Typography sx={{ mb: 0.5 }} variant='h5' color='text.primary'>
            No activity yet
          </Typography>
          <Typography variant='body1'>
            Customer updates will show up here.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1 }}>
          <NotificationsList
            items={items}
            onOpen={open}
            onToggleRead={toggleRead}
          />
        </Box>
      )}
    </Box>
  );
};

export default NotificationsPage;
